'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useFaqCount } from '@/lib/faq-hooks';
import { ChevronDown, Gift, Sparkles, Clock } from 'lucide-react';
import { NavPreview } from '@/components/public/nav-preview';
import { Footer } from '@/components/public/footer';
import { VoucherAccordion } from './voucher-accordion';
import { ValueVoucherForm } from './value-voucher-form';
import { PackageVoucherForm } from './package-voucher-form';
import { SpaPassForm } from './spa-pass-form';
import type { VoucherRoomTypeOption } from '@/lib/gift-voucher-pricing';

const HERO_IMAGE = '/design-images/uploads/1777533884771-5_dc_swan-suite_3.jpg';

type AccordionId = 'value' | 'package' | 'spa_pass' | `faq_${number}`;

interface GiftVouchersClientProps {
  roomTypes: VoucherRoomTypeOption[];
}

export function GiftVouchersClient({ roomTypes }: GiftVouchersClientProps) {
  const t = useTranslations('giftVouchers');
  const faqCount = useFaqCount('giftVouchers.faq');
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [openId, setOpenId] = useState<AccordionId | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const toggle = (id: AccordionId) => setOpenId((prev) => (prev === id ? null : id));

  const faqItems = Array.from({ length: faqCount }, (_, idx) => idx + 1).map((i) => ({
    id: `faq_${i}` as AccordionId,
    q: t(`faq.q${i}`),
    a: [t(`faq.a${i}_p1`), t(`faq.a${i}_p2`)],
  }));

  // overflow-x-clip (not -hidden) on <main>: `hidden` would force overflow-y to a
  // scroll container, which breaks the package form's `lg:sticky` summary. `clip`
  // contains horizontal overflow without establishing a scroll container.
  return (
    <main className="min-h-screen bg-white text-gray-900 overflow-x-clip">
      <NavPreview />

      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src={HERO_IMAGE}
            alt={t('hero.imageAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: '50% 30%' }}
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />

        <div
          className={`relative h-full flex flex-col items-center justify-center px-6 text-center transition-all duration-[2000ms] ease-out ${
            heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Gift className="w-10 h-10 text-white/80 mb-6" strokeWidth={1.2} />
          <h1
            className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight"
            style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}
          >
            {t('hero.title')}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[2px] w-20 bg-[#daa520]" />
            <p
              className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 break-words"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
            >
              {t('hero.subtitle')}
            </p>
            <div className="h-[2px] w-20 bg-[#daa520]" />
          </div>
          <p
            className="text-lg sm:text-2xl md:text-3xl text-white/95 max-w-3xl mx-auto font-light"
            style={{ textShadow: '1px 1px 8px rgba(0,0,0,0.5)' }}
          >
            {t('hero.tagline')}
          </p>

          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs uppercase tracking-widest">{t('hero.discover')}</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#faf8f6] py-16 md:py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto space-y-4">
          <p className="text-lg md:text-xl leading-relaxed text-gray-700">
            <span className="font-semibold text-[#801515] tracking-wide">{t('intro').split(' ')[0]}</span>{' '}
            {t('intro').split(' ').slice(1).join(' ')}
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-gray-700">
            {t('introPersonalized').split(' ').map((word, idx, arr) => {
              const isKeyword = /personalized|personalizate|személyre/i.test(word);
              return (
                <span key={idx}>
                  {isKeyword && <span className="font-semibold text-[#801515]">{word}</span>}
                  {!isKeyword && word}
                  {idx < arr.length - 1 && ' '}
                </span>
              );
            })}
          </p>
        </div>
      </section>

      {/* ACCORDIONS */}
      <section className="py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto space-y-5">
          <VoucherAccordion
            isOpen={openId === 'value'}
            onToggle={() => toggle('value')}
            badge={<><Gift className="w-3.5 h-3.5" /> {t('flexible.badge')}</>}
            title={t('flexible.title')}
            shortDescription={t('flexible.description')}
            validity={t('flexible.validity')}
            accent="#801515"
          >
            <ValueVoucherForm />
          </VoucherAccordion>

          <VoucherAccordion
            isOpen={openId === 'package'}
            onToggle={() => toggle('package')}
            badge={<><Sparkles className="w-3.5 h-3.5" /> {t('curated.badge')}</>}
            title={t('curated.title')}
            shortDescription={t('curated.description')}
            validity={t('curated.validity')}
            accent="#daa520"
          >
            <PackageVoucherForm roomTypes={roomTypes} />
          </VoucherAccordion>

          <VoucherAccordion
            isOpen={openId === 'spa_pass'}
            onToggle={() => toggle('spa_pass')}
            badge={<><Clock className="w-3.5 h-3.5" /> {t('spa.pass')}</>}
            title={t('spa.title')}
            shortDescription={t('spa.claimNote')}
            validity={t('spa.microcopy')}
            accent="#478B8B"
          >
            <SpaPassForm />
          </VoucherAccordion>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.title')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subtitle')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden observe-me slide-up transition-all duration-300 hover:shadow-2xl"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  aria-expanded={openId === item.id}
                  className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-[#faf8f6] transition-colors duration-300"
                >
                  <span className="font-poppins text-xl md:text-2xl font-semibold text-gray-900 pr-4">
                    {item.q}
                  </span>
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full bg-[#801515] flex items-center justify-center text-white transition-transform duration-500 ${
                      openId === item.id ? 'rotate-180' : ''
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div
                  className={`transition-all duration-500 ease-in-out ${
                    openId === item.id ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-8 pb-6 pt-2">
                    <div className="w-16 h-1 bg-[#801515] mb-4" />
                    <div className="space-y-3">
                      {item.a.map((paragraph, idx) => (
                        <p key={idx} className="text-lg text-gray-600 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
